/**
 * define.ts — как объявляется словарь раздела (WEB-1874, этап 2).
 *
 * Правило «текст кабинета живёт в словаре» имеет ДВА конца: английская
 * половина и русская. Ручной список тут не годится по построению — он молчит
 * про новый ключ (шрам WEB-1687). Поэтому вторая половина не описывается
 * своим типом, а ВЫВОДИТСЯ из первой: `{ [K in keyof EN]: EN[K] }`. Пропуск
 * ключа, лишний ключ и другая сигнатура функции подстановки становятся
 * ошибкой компиляции в обе стороны, бесплатно и без сторожа.
 *
 * Прогонный сторож (`__tests__/cabinet-strings-locale.web1874.test.ts`)
 * добирает то, чего тип не видит: пустые значения, значение-заглушку, равную
 * ключу, и мёртвые ключи.
 *
 * Как пользоваться:
 *   в компоненте  — `const s = useStrings(billingStrings); <Button>{s.cancel}</Button>`
 *   вне React     — `pickStrings(errorsStrings).somethingFailed`
 */
import { getUiLocale, useUiLocale, type UiLocale } from './locale';

/** Значения словаря: готовая строка либо подстановка-функция. */
export type DictionaryShape = Record<
  string,
  string | ((...args: never[]) => string)
>;

export interface Dictionary<EN extends DictionaryShape> {
  readonly en: EN;
  readonly ru: { [K in keyof EN]: EN[K] };
}

/**
 * Объявляет словарь раздела.
 * @param en английская половина — она задаёт множество ключей и сигнатуры.
 * @param ru русская половина — её тип выведен из английской.
 */
export function defineDictionary<EN extends DictionaryShape>(
  en: EN,
  ru: { [K in keyof EN]: EN[K] },
): Dictionary<EN> {
  return { en, ru };
}

/** Половина словаря по языку кабинета. Для компонентов. */
export function useStrings<EN extends DictionaryShape>(
  dictionary: Dictionary<EN>,
): EN {
  const locale = useUiLocale();
  return dictionary[locale] as EN;
}

/** Половина словаря для кода вне React (thunk'и, утилиты, сервисы). */
export function pickStrings<EN extends DictionaryShape>(
  dictionary: Dictionary<EN>,
  locale: UiLocale = getUiLocale(),
): EN {
  return dictionary[locale] as EN;
}
